import React from "react";
import { useTranslation } from "react-i18next";
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import {
  Card, CardHeader, CardSubHeader, Header, LinkButton, Row, StatusTable, SubmitBar, EditIcon, Loader
} from "@egovernments/digit-ui-react-components";
import Timeline from "../../../components/Timeline";
import { convertEpochToDateDMY, getOrderDocuments } from "../../../utils";
import DocumentsPreview from "../../../../../templates/ApplicationDetails/components/DocumentsPreview";

const CheckPage = ({ onSubmit, formData, currStep }) => {
  const applicationFlow = Digit?.SessionStorage.get("CITIZEN.COMMON.HOME.FLOW") || null;
  const tcpCode = applicationFlow === 'TCP';
  const udCode = applicationFlow === 'UD';

  const { t } = useTranslation();
  const stateId = Digit.ULBService.getStateId();
  const BusinessService = (formData?.businessService === "BPA_FORM26") ? "BPA.FORM_26_FEE" : "BPA.FORM_26_FEE_UD";
  const {
    applicationNo,
    landAreaDetails,
    descriptionOfLand,
    checklist,
    landInfo,
    nocDocuments,
    PrevStateDocuments,
    PrevStateNocDocuments,
  } = formData;
  const owners = landInfo?.owners;
  const ownerPermanentAddress = owners?.[0]?.permanentAddress;
  const docs = formData?.documents;
  const documents = typeof docs === 'object' && !Array.isArray(docs) ? Object.values(docs) : docs;
  formData.documents = documents;

  // for application documents
  let improvedDoc = [];
  PrevStateDocuments?.map(preDoc => { improvedDoc.push({ ...preDoc, module: "OBPS" }) });
  documents?.map(appDoc => { improvedDoc.push({ ...appDoc, module: "OBPS" }) });

  //for NOC documents 
  PrevStateNocDocuments?.map(preNocDoc => { improvedDoc.push({ ...preNocDoc, module: "NOC" }) });
  nocDocuments?.nocDocuments?.map(nocDoc => { improvedDoc.push({ ...nocDoc, module: "NOC" }) });

  const { data: pdfDetails, isLoading: pdfLoading } = Digit.Hooks.useDocumentSearch(improvedDoc, { enabled: improvedDoc?.length > 0 ? true : false });

  let applicationDocs = [], nocAppDocs = [];
  if (pdfDetails?.pdfFiles?.length > 0) {
    pdfDetails?.pdfFiles?.map(pdfAppDoc => {
      if (pdfAppDoc?.module == "OBPS") applicationDocs.push(pdfAppDoc);
      if (pdfAppDoc?.module == "NOC") nocAppDocs.push(pdfAppDoc);
    });
  }

  let consumerCode = formData?.applicationNo;
  const fetchBillParams = { consumerCode };

  const { data: paymentDetails } = Digit.Hooks.useFetchBillsForBuissnessService(
    { businessService: BusinessService, ...fetchBillParams, tenantId: stateId },
    {
      enabled: consumerCode ? true : false,
      retry: false,
    }
  );
  const totalAmount = paymentDetails?.Bill?.[0]?.billDetails[0]?.amount
  /*
  let processingFee
  let eCharges
  if (totalAmount !== undefined) {
    processingFee = +(0.97 * totalAmount).toFixed(2);
    eCharges = +(0.03 * totalAmount).toFixed(2);
  }
  */

  const isEditApplication = window.location.href.includes("editApplication");
  const sendbacktocitizenApp = window.location.href.includes("sendbacktocitizen");
  const { pathname } = useLocation();
  const basePath = pathname.split('/').slice(0, -1).join('/');
  let routeLink = basePath; // `/digit-ui/citizen/obps/${formData?.applicationType?.toLowerCase().split("_").join('')}`;
  if (isEditApplication) routeLink = `/digit-ui/citizen/obps/editApplication/form26/${formData?.tenantId}/${formData?.applicationNo}`;
  if (sendbacktocitizenApp) routeLink = `/digit-ui/citizen/obps/sendbacktocitizen/form26/${formData?.tenantId}/${formData?.applicationNo}`;

  function routeTo(jumpTo) {
    location.href = jumpTo;
  }

  if (pdfLoading) {
    return <Loader />
  }

  if (!applicationNo) {
    return (
      <React.Fragment>
        <Timeline flow="Form11" currentStep={currStep.step} />

        <Card style={{ paddingRight: "1rem", marginTop: '1rem' }}>
          <Box display="flex" justifyContent="center">
            <Header>{t("BPA_STEPPER_SUMMARY_HEADER")}</Header>
          </Box>
          <hr style={{ backgroundColor: "#cccccc", height: "2px", marginBottom: "20px" }} />
          <CardHeader styles={{ textAlign: 'center' }}>No Application Data Available.</CardHeader>
        </Card>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Timeline flow="Form26" currentStep={currStep.step} />

      <Card style={{ paddingRight: "1rem", marginTop: '1rem' }}>
        <Box display="flex" justifyContent="center">
          <Header>{t("BPA_STEPPER_SUMMARY_HEADER")}</Header>
        </Box>
        <hr style={{ backgroundColor: "#cccccc", height: "2px", marginBottom: "20px" }} />

        <CardHeader>{t(`BPA_BASIC_DETAILS_TITLE`)}</CardHeader>
        <StatusTable style={{ margin: '0 1rem' }}>
          <Row className="border-none" label={t(`BPA_APPLICATION_NUMBER_LABEL`)} text={applicationNo || ""} />
          <Row className="border-none" label={t(`BPA_BASIC_DETAILS_APP_DATE_LABEL`)} text={convertEpochToDateDMY(Number(formData?.auditDetails?.createdTime))} />
          <Row className="border-none" label={t(`BPA_BASIC_DETAILS_APPLICATION_TYPE_LABEL`)} text={t(`WF_BPA_${formData?.applicationType}`)} />
          <Row className="border-none" label={t(`BPA_BASIC_DETAILS_APPLICATION_NAME_LABEL`)} text={owners?.[0]?.name} />
        </StatusTable>
      </Card>
      <Card style={{ paddingRight: "1rem" }}>
        <CardHeader styles={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>{t("BPA_STEPPER_LAND_INFORMATION")}</Box>
          <Box>
            <LinkButton
              label={<EditIcon />}
              style={{ display: "inline" }}
              onClick={() => routeTo(`${routeLink}/land-area-details`)}
            />
          </Box>
        </CardHeader>

        <StatusTable style={{ margin: '0 1rem' }}>
          <CardSubHeader style={{ fontSize: 20, marginLeft: 0 }}>{t("BPA_TAB_LAND_AREA_DETAILS")}</CardSubHeader>
          {landAreaDetails?.city && (
            <React.Fragment>
              <Row className="border-none" label={t(`BPA_DISTRICT_LABEL`)} text={landAreaDetails?.city || t("CS_NA")} />
              {tcpCode && <Row className="border-none" label={t(`BPA_LOC_NOTIFIED-AREA_LABEL`)} text={landAreaDetails?.locality?.name || t("CS_NA")} />}
              {udCode && <Row className="border-none" label={t(udCode ? `BPA_ULB-AREA_LABEL` : `BPA_AREA_TYPE_LABEL`)} text={landAreaDetails?.areaType || t("CS_NA")} />}
              {udCode && <Row className="border-none" label={t(udCode ? `BPA_LOC_WARD-AREA_LABEL` : `BPA_LOC_VILLAGE-AREA_LABEL`)} text={landAreaDetails?.village?.name || t("CS_NA")} />}
            </React.Fragment>
          )}

          <hr style={{ backgroundColor: "#cccccc", height: "2px", marginTop: "20px", marginBottom: "20px" }} />

          <CardSubHeader style={{ fontSize: 20, marginLeft: 0 }}>{t("BPA_TAB_DESCRIPTION_OF_LAND")}</CardSubHeader>
          <Row className="border-none" label={t(`BPA_DOL_REVENUE_VILLAGE_MOHALLA_LABEL`)} text={descriptionOfLand?.revenueVillageMohal || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_DOL_KHATA_NO_LABEL`)} text={descriptionOfLand?.khataNo || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_DOL_KHATAUNI_NO_LABEL`)} text={descriptionOfLand?.khatauniNo || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_LAT_LONG_LABEL`)} text={descriptionOfLand?.latitude ? `${descriptionOfLand?.latitude} - ${descriptionOfLand?.longitude}` : t("CS_NA")} />
          <Box sx={{ border: '1px solid #d6d5d4', padding: '1rem 1rem 0', marginBottom: '1rem' }}>
            {descriptionOfLand?.khasraWithArea?.map(({ khasraNo, area }, index) => (
              <Box key={`${khasraNo}_${area}_${index}`} sx={{
                paddingBottom: '.5rem',
                display: 'flex',
                ...((descriptionOfLand?.khasraWithArea.length !== index + 1) && {
                  borderBottom: '1px solid #d6d5d4',
                  marginBottom: '1rem',
                })
              }}
              >
                <Row
                  labelStyle={{ whiteSpace: 'nowrap', flex: 1 }}
                  textStyle={{ maxWidth: '35%' }}
                  className="border-none"
                  label={t(`BPA_DOL_KHASARA_NO_LABEL`)}
                  text={khasraNo || t("CS_NA")}
                  rowContainerStyle={{ flex: 1, marginBottom: 0 }}
                />
                <Row
                  labelStyle={{ whiteSpace: 'nowrap', flex: 1 }}
                  textStyle={{ maxWidth: '35%' }}
                  className="border-none"
                  label={t(`BPA_DOL_AREA_SQM_LABEL`)}
                  text={area || t("CS_NA")}
                  rowContainerStyle={{ flex: 1, marginBottom: 0 }}
                />
              </Box>
            ))}
          </Box>
          <Row className="border-none" label={t(`BPA_DOL_TOTAL_PLOT_AREA_LABEL`)} text={descriptionOfLand?.totalPlotArea || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_DOL_NO_OF_PLOT_LABEL`)} text={descriptionOfLand?.noOfPlot || t("CS_NA")} />
          <Row className="border-none" label={t(`CORE_COMMON_PROPERTY_TYPE`)} text={descriptionOfLand?.propertyType?.name || t("CS_NA")} />
          <Row className="border-none" label={t(`CORE_COMMON_USE_TYPE`)} text={descriptionOfLand?.useType?.name || t("CS_NA")} />
          <Row className="border-none" label={t(`CORE_COMMON_PLOT_AREA`)} text={descriptionOfLand?.plotArea || t("CS_NA")} />
          <Row className="border-none" label={t(`CORE_COMMON_PERMISSBLE_COVERAGE`)} text={descriptionOfLand?.maximumCoverage || t("CS_NA")} />

          <CardSubHeader style={{ fontSize: 20, marginLeft: 0 }}>{t("BPA_DETAILS_OF_PLOT_AFTER_SUBDIVISION")}</CardSubHeader>

          <Row className="border-none" label={t(`BPA_TYPE_OF_LAND_VOLVED`)} text={descriptionOfLand?.typeOfLandVolved?.name || t("CS_NA")} />
          {descriptionOfLand?.typeOfLandVolved?.name === "Yes" && (
            <React.Fragment>
              <Row className="border-none" label={t(`BPA_TYPE_OF_CLU_INVOLVED`)} text={descriptionOfLand?.typeOfCLUInvolved?.name || t("CS_NA")} />
              <div className="table table-bordered table-responsive">
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th className="fw-normal" style={{ minWidth: 50 }}>{`${t("BPA_SHORT_SERIAL_NO")}`}</th>
                      <th className="fw-normal">{`${t("BPA_PLOT_NO")} *`}</th>
                      <th className="fw-normal">{`${t("BPA_PLOT_AREA")} *`}</th>
                      <th className="fw-normal">{`${t("BPA_TYPE_OF_LAND_DEVELOPMENT")} *`}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {descriptionOfLand?.khasraTable?.map((field, index) => {
                      return (
                        <tr key={field.id}>
                          <th className="fw-normal" style={{ minWidth: 50 }}>{index + 1}</th>
                          <td>{field.plotNo}</td>
                          <td>{field.plotArea}</td>
                          <td>{field.typeOfLand?.name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          )}

          <Row className="border-none" label={t(`CORE_COMMON_NO_OF_BLOCK`)} text={descriptionOfLand?.noOfBlocks || t("CS_NA")} />
          {descriptionOfLand?.buildUpAreaTable?.map(({ table }, idx) => (
            <div className="table table-bordered table-responsive" style={{ display: "block" }} key={`table_${idx}`}>
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th className="fw-normal" style={{ minWidth: 50 }}>{`${t("BPA_SHORT_SERIAL_NO")}`}</th>
                    <th className="fw-normal" style={{ minWidth: 100 }}>{`${t("BPA_FLOOR_TYPE")} *`}</th>
                    <th className="fw-normal">{`${t("BPA_APPROVED_FLOOR_AREA")} *`}</th>
                    <th className="fw-normal">{`${t("BPA_LAND_USE_TYPE")} *`}</th>
                    <th className="fw-normal">{`${t("BPA_PARKING_AREA")}`}</th>
                    <th className="fw-normal">{`${t("BPA_ACTUAL_CONSTRUCTION")}`}</th>
                    <th className="fw-normal">{`${t("BPA_PERMISSIBLE_DEVIATION")}`}</th>
                    <th className="fw-normal">{`${t("BPA_SETBACK_DEVIATION")}`}</th>
                    <th className="fw-normal">{`${t("DEVIATION_CENT")}`}</th>
                  </tr>
                </thead>
                <tbody>
                  {table?.map((field, index) => (
                    <tr key={`row_${idx}_${index}`}>
                      <th className="fw-normal" style={{ minWidth: 50 }}>{index + 1}</th>
                      <td style={{ minWidth: 100 }}>{field.floorType?.name}</td>
                      <td>{field.floorArea}</td>
                      <td>{field.landUseType?.name}</td>
                      <td>{field.parkingArea}</td>
                      <td>{field.actualConstruction}</td>
                      <td>{field.permissibleDeviation}</td>
                      <td>{field.setbackDeviation}</td>
                      <td>{field.deviation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <hr style={{ backgroundColor: "#cccccc", height: "2px", marginTop: "20px", marginBottom: "20px" }} />

          <CardSubHeader style={{ fontSize: 20, marginLeft: 0 }}>{t("BPA_TAB_CHECKLIST")}</CardSubHeader>
          <Row
            labelStyle={{ flex: 1 }}
            textStyle={{ width: '10%' }}
            className="border-none"
            label={t(`BPA_CHECKLIST_OPTION_PADESTERIAN_MIN_WIDTH`)}
            text={checklist?.pedestrianMinWidth || t("CS_NA")}
          />
          <Row
            labelStyle={{ flex: 1 }}
            textStyle={{ width: '10%' }}
            className="border-none"
            label={t(`BPA_CHECKLIST_OPTION_VEHICULAR_ACCESS_MIN_WIDTH`)}
            text={checklist?.vehicularAccessMinWidth || t("CS_NA")}
          />
          <Row
            labelStyle={{ flex: 1 }}
            textStyle={{ width: '10%' }}
            className="border-none"
            label={t(`BPA_CHECKLIST_OPTION_OPEN_GREEN_SPACE_MIN_AREA`)}
            text={checklist?.openGreenSpaceMinArea || t("CS_NA")}
          />
          <Row
            labelStyle={{ flex: 1 }}
            textStyle={{ width: '10%' }}
            className="border-none"
            label={t(`BPA_CHECKLIST_OPTION_SOAK_PIT_MIN_AREA`)}
            text={checklist?.soakPitMinArea || t("CS_NA")}
          />
          <Row
            labelStyle={{ flex: 1 }}
            textStyle={{ width: '10%' }}
            className="border-none"
            label={t(`BPA_CHECKLIST_OPTION_PLOT_ORIENTATION`)}
            text={checklist?.plotOrientation || t("CS_NA")}
          />
          <Row
            labelStyle={{ flex: 1 }}
            textStyle={{ width: '10%' }}
            className="border-none"
            label={t(`BPA_CHECKLIST_OPTION_PLOT_LAYOUT`)}
            text={checklist?.plotLayout || t("CS_NA")}
          />
        </StatusTable>
      </Card>
      <Card style={{ paddingRight: "1rem" }}>
        <CardHeader styles={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>{t("BPA_NEW_TRADE_DETAILS_HEADER_DETAILS")}</Box>
          <Box>
            <LinkButton
              label={<EditIcon />}
              style={{ display: "inline" }}
              onClick={() => routeTo(`${routeLink}/land-area-details`)}
            />
          </Box>
        </CardHeader>
        <StatusTable style={{ margin: '0 1rem' }}>
          <Row className="border-none" label={t(`BPA_CITY_LABEL`)} text={ownerPermanentAddress?.town || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_LOC_MOHALLA_LABEL`)} text={ownerPermanentAddress?.tehsil || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_DISTRICT_LABEL`)} text={ownerPermanentAddress?.district || t("CS_NA")} />
          <Row className="border-none" label={t(`CORE_COMMON_STATE`)} text={ownerPermanentAddress?.state || t("CS_NA")} />
          <Row className="border-none" label={t(`BPA_DETAILS_PIN_LABEL`)} text={ownerPermanentAddress?.pinno || t("CS_NA")} />
        </StatusTable>
      </Card>
      <Card style={{ paddingRight: "1rem" }}>
        <CardHeader styles={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>{t("BPA_APPLICANT_DETAILS_HEADER")}</Box>
          <Box>
            <LinkButton
              label={<EditIcon />}
              style={{ display: "inline" }}
              onClick={() => routeTo(`${routeLink}/owner-details`)}
            />
          </Box>
        </CardHeader>
        {owners?.map((ob, index) => (
          <div key={index} style={owners?.length > 1 ? { marginTop: "19px", background: "#FAFAFA", border: "1px solid #D6D5D4", borderRadius: "4px", padding: "8px", lineHeight: "19px", maxWidth: "960px", minWidth: "280px" } : {}}>
            {owners.length > 1 && <CardSubHeader>{t("COMMON_OWNER")} {index + 1}</CardSubHeader>}
            <StatusTable style={{ margin: '0 1rem' }}>
              <Row className="border-none" textStyle={index == 0 && owners.length == 1 ? { paddingLeft: "12px" } : {}} label={t(`CORE_COMMON_NAME`)} text={ob?.name} />
              <Row className="border-none" label={t(`BPA_APPLICANT_GENDER_LABEL`)} text={t(ob?.gender)} />
              <Row className="border-none" label={t(`CORE_COMMON_MOBILE_NUMBER`)} text={ob?.mobileNumber} />
              <Row className="border-none" label={t(`BPA_APPLICANT_EMAIL_LABEL`)} text={ob?.emailId} />
              <Row className="border-none" label={t(`BPA_IS_PRIMARY_OWNER_LABEL`)} text={`${ob?.isPrimaryOwner ? 'Yes' : 'No'}`} />
            </StatusTable>
          </div>))}
      </Card>
      <Card style={{ paddingRight: "1rem" }}>
        <CardHeader styles={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>{t("BPA_DOCUMENT_DETAILS_LABEL")}</Box>
          <Box>
            <LinkButton
              label={<EditIcon />}
              style={{ display: "inline" }}
              onClick={() => routeTo(`${routeLink}/document-details`)}
            />
          </Box>
        </CardHeader>
        {<DocumentsPreview documents={getOrderDocuments(applicationDocs)} svgStyles={{}} isSendBackFlow={false} isHrLine={true} titleStyles={{ fontSize: "18px", lineHeight: "24px", "fontWeight": 700, marginBottom: "10px" }} />}
      </Card>
      <Card style={{ paddingRight: "1rem", marginBottom: '1rem' }}>
        <CardHeader>{t("BPA_SUMMARY_FEE_EST")}</CardHeader>
        <StatusTable style={{ margin: '0 1rem' }}>
          <Row className="border-none" label={t(`BPA_COMMON_APPLICATION_FEE`)} text={`₹ ${totalAmount || "0"}`} />
          {/*
          <Row className="border-none" label={t(`BPA_COMMON_PROCESSING_FEE`)} text={`₹ ${processingFee || "0"}`} />
          {eCharges > 0 && <Row className="border-none" label={t(`BPA_COMMON_ECHARGES`)} text={`₹ ${eCharges || "0"}`} />}
          */}
        </StatusTable>
        <hr style={{ backgroundColor: "#cccccc", height: "2px", marginTop: "20px", marginBottom: "20px" }} />
        <CardHeader styles={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>{t("BPA_COMMON_TOTAL_AMT")}</div>
          <div>₹ {totalAmount || "0"}</div>
        </CardHeader>
        <SubmitBar label={t("BPA_SEND_TO_STRUCTURAL_ENGINEER")} onSubmit={onSubmit} />
      </Card>
    </React.Fragment>
  );
};

export default CheckPage;
